import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Search,
  RefreshCcw,
  CheckCircle2,
  AlertTriangle,
  CircleSlash,
  X,
  Mail,
  TestTube2,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_META = {
  sent: {
    label: "Elküldve",
    tone: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    icon: CheckCircle2,
  },
  failed: {
    label: "Hiba",
    tone: "bg-rose-50 text-rose-700 ring-rose-200",
    icon: AlertTriangle,
  },
  skipped: {
    label: "Kihagyva",
    tone: "bg-amber-50 text-amber-700 ring-amber-200",
    icon: CircleSlash,
  },
  disabled: {
    label: "Sablon ki",
    tone: "bg-slate-100 text-slate-600 ring-slate-200",
    icon: CircleSlash,
  },
  no_recipients: {
    label: "Nincs címzett",
    tone: "bg-slate-100 text-slate-600 ring-slate-200",
    icon: CircleSlash,
  },
  template_missing: {
    label: "Sablon hiányzik",
    tone: "bg-rose-50 text-rose-700 ring-rose-200",
    icon: AlertTriangle,
  },
  render_failed: {
    label: "Render hiba",
    tone: "bg-rose-50 text-rose-700 ring-rose-200",
    icon: AlertTriangle,
  },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || {
    label: status || "?",
    tone: "bg-slate-100 text-slate-600 ring-slate-200",
    icon: CircleSlash,
  };
  const Icon = meta.icon;
  return (
    <span
      data-testid={`email-log-status-${status}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium ring-1 ${meta.tone}`}
    >
      <Icon className="h-3 w-3" strokeWidth={1.75} />
      {meta.label}
    </span>
  );
}

function formatDateTimeHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

export function EmailAuditLog({ templates = [] }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [eventFilter, setEventFilter] = useState("__all__");
  const [recipient, setRecipient] = useState("");
  const [testFilter, setTestFilter] = useState("__all__"); // __all__ | live | test
  const [openLog, setOpenLog] = useState(null);
  const [fullLog, setFullLog] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);

  const params = useMemo(() => {
    const p = { limit: 100 };
    if (statusFilter !== "__all__") p.status = statusFilter;
    if (eventFilter !== "__all__") p.event = eventFilter;
    if (recipient.trim()) p.recipient = recipient.trim();
    if (testFilter === "test") p.is_test = true;
    if (testFilter === "live") p.is_test = false;
    return p;
  }, [statusFilter, eventFilter, recipient, testFilter]);

  const load = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        api.get("/notifications/logs", { params }),
        api.get("/notifications/logs/stats"),
      ]);
      setItems(logsRes.data.items || []);
      setTotal(logsRes.data.total || 0);
      setStats(statsRes.data || null);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a naplót."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, eventFilter, testFilter]);

  // Debounced recipient search
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipient]);

  const openDetail = async (log) => {
    setOpenLog(log);
    setFullLog(null);
    setOpenLoading(true);
    try {
      const { data } = await api.get(`/notifications/logs/${log._id}`);
      setFullLog(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült megnyitni a bejegyzést."
      );
      setOpenLog(null);
    } finally {
      setOpenLoading(false);
    }
  };

  const closeDetail = () => {
    setOpenLog(null);
    setFullLog(null);
  };

  const recent = stats?.recent_30d || {};
  const totalRecent = Object.values(recent).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="email-audit-log">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Összes (30 nap)" value={totalRecent} tone="slate" />
        <StatCard
          label="Elküldve"
          value={recent.sent || 0}
          tone="emerald"
          icon={CheckCircle2}
        />
        <StatCard
          label="Hiba"
          value={(recent.failed || 0) + (recent.render_failed || 0) + (recent.template_missing || 0)}
          tone="rose"
          icon={AlertTriangle}
        />
        <StatCard
          label="Kihagyva"
          value={(recent.skipped || 0) + (recent.disabled || 0) + (recent.no_recipients || 0)}
          tone="amber"
          icon={CircleSlash}
        />
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
          <Filter className="h-3 w-3" />
          Szűrők
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="Címzett keresése..."
              className="pl-8"
              data-testid="email-log-recipient-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="email-log-status-filter">
              <SelectValue placeholder="Státusz" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Minden státusz</SelectItem>
              <SelectItem value="sent">Elküldve</SelectItem>
              <SelectItem value="failed">Hiba</SelectItem>
              <SelectItem value="skipped">Kihagyva</SelectItem>
              <SelectItem value="disabled">Sablon kikapcsolva</SelectItem>
              <SelectItem value="no_recipients">Nincs címzett</SelectItem>
              <SelectItem value="template_missing">Sablon hiányzik</SelectItem>
              <SelectItem value="render_failed">Render hiba</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger data-testid="email-log-event-filter">
              <SelectValue placeholder="Esemény" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Minden esemény</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.event} value={t.event}>
                  {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={testFilter} onValueChange={setTestFilter}>
            <SelectTrigger data-testid="email-log-test-filter">
              <SelectValue placeholder="Típus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tényleges + Teszt</SelectItem>
              <SelectItem value="live">Csak tényleges</SelectItem>
              <SelectItem value="test">Csak teszt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
          <span>
            {loading ? "Betöltés..." : <>{total} találat</>}
          </span>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-slate-500 transition-colors hover:text-slate-900"
            data-testid="email-log-refresh"
          >
            <RefreshCcw className="h-3 w-3" />
            Frissítés
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center px-6 py-12 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Betöltés...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-slate-50">
              <Mail className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium text-slate-700">
              Nincs napló bejegyzés a megadott szűrőkkel
            </p>
            <p className="text-xs text-slate-400">
              Próbálj eltávolítani vagy módosítani néhány szűrőt.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-[10.5px] font-medium uppercase tracking-[0.12em] text-slate-500">
                <th className="px-4 py-2.5">Időpont</th>
                <th className="px-4 py-2.5">Sablon · Esemény</th>
                <th className="px-4 py-2.5">Címzettek</th>
                <th className="px-4 py-2.5">Tárgy</th>
                <th className="px-4 py-2.5">Státusz</th>
              </tr>
            </thead>
            <tbody>
              {items.map((log) => (
                <tr
                  key={log._id}
                  onClick={() => openDetail(log)}
                  data-testid={`email-log-row-${log._id}`}
                  className="cursor-pointer border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/60"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-[12px] font-mono text-slate-500">
                    {formatDateTimeHu(log.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-medium text-slate-800">
                          {log.template_title || "—"}
                        </div>
                        <div className="truncate text-[11px] font-mono text-slate-400">
                          {log.event}
                        </div>
                      </div>
                      {log.is_test && (
                        <Badge
                          variant="secondary"
                          className="ml-1 gap-1 bg-amber-50 text-amber-700 hover:bg-amber-50"
                        >
                          <TestTube2 className="h-3 w-3" />
                          Teszt
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[12.5px] text-slate-700">
                    <div className="max-w-[260px] truncate font-mono">
                      {(log.recipients || []).join(", ") || "—"}
                    </div>
                    {(log.cc || []).length > 0 && (
                      <div className="max-w-[260px] truncate font-mono text-[11px] text-slate-400">
                        CC: {log.cc.join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12.5px] text-slate-700">
                    <div className="max-w-[320px] truncate">
                      {log.subject || <span className="text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <StatusBadge status={log.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={Boolean(openLog)} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent
          className="max-w-3xl"
          data-testid="email-log-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {openLog?.template_title || "Email napló"}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 text-[11px]">
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono">
                {openLog?.event}
              </code>
              {openLog && <StatusBadge status={openLog.status} />}
              {openLog?.is_test && (
                <Badge
                  variant="secondary"
                  className="gap-1 bg-amber-50 text-amber-700"
                >
                  <TestTube2 className="h-3 w-3" />
                  Teszt
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          {openLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés...
            </div>
          ) : (
            fullLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <Field label="Időpont" value={formatDateTimeHu(fullLog.created_at)} />
                  <Field
                    label="Indította"
                    value={fullLog.triggered_by_name || "rendszer"}
                  />
                  <Field
                    label="Címzettek"
                    value={(fullLog.recipients || []).join(", ") || "—"}
                    mono
                  />
                  <Field
                    label="CC"
                    value={(fullLog.cc || []).join(", ") || "—"}
                    mono
                  />
                  <Field
                    label="Provider azonosító"
                    value={fullLog.provider_id || "—"}
                    mono
                  />
                  <Field
                    label="Kategória"
                    value={fullLog.category || "—"}
                  />
                </div>

                {fullLog.error && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-[12px]">
                    <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-rose-700">
                      Hiba
                    </div>
                    <code className="font-mono text-rose-800">
                      {fullLog.error}
                    </code>
                  </div>
                )}

                <div>
                  <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Tárgy
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 font-mono text-[12px]">
                    {fullLog.subject || "—"}
                  </div>
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Üzenet (renderelt HTML)
                    </span>
                  </div>
                  <div
                    data-testid="email-log-body-preview"
                    className="max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white p-4 text-[12px] [&_*]:max-w-full"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(fullLog.body_html || "<em>üres</em>", {
                        // Email templates need inline styles + tables; strip scripts/handlers.
                        ALLOWED_TAGS: ["a", "b", "br", "div", "em", "h1", "h2", "h3", "i", "img", "li", "ol", "p", "span", "strong", "style", "table", "tbody", "td", "th", "thead", "tr", "ul", "small", "hr", "u"],
                        ALLOWED_ATTR: ["href", "src", "alt", "title", "style", "width", "height", "align", "valign", "cellpadding", "cellspacing", "border", "bgcolor", "color", "class", "id", "target", "rel"],
                      }),
                    }}
                  />
                </div>

                {Object.keys(fullLog.context || {}).length > 0 && (
                  <details className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-[12px]">
                    <summary className="cursor-pointer text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Kontextus változók
                    </summary>
                    <pre className="mt-2 overflow-auto rounded bg-white p-3 font-mono text-[11px] text-slate-700">
                      {JSON.stringify(fullLog.context, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
        {label}
      </div>
      <div
        className={`text-[12.5px] text-slate-800 ${mono ? "font-mono" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, icon: Icon }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50/50",
    emerald: "border-emerald-200 bg-emerald-50/60",
    rose: "border-rose-200 bg-rose-50/60",
    amber: "border-amber-200 bg-amber-50/60",
  };
  const iconColor = {
    emerald: "text-emerald-600",
    rose: "text-rose-600",
    amber: "text-amber-600",
    slate: "text-slate-500",
  };
  return (
    <div
      className={`rounded-2xl border p-3.5 shadow-sm ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
          {label}
        </span>
        {Icon && <Icon className={`h-3.5 w-3.5 ${iconColor[tone] || iconColor.slate}`} />}
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
        {value}
      </div>
    </div>
  );
}
